import React, { useState } from 'react';
import { Shield, X, ChevronDown, ChevronUp, Check } from 'lucide-react';
import { useAccountMaturity } from '../hooks/useTrustScore';
import { ACCOUNT_AGE } from '../lib/trustScore';

const DISMISSED_KEY = 'bchnostr:new-account-banner-dismissed';

interface NewAccountBannerProps {
  pubkey: string | undefined;
}

/**
 * Shows a friendly informational banner for new accounts
 * explaining the anti-spam restrictions and what they can do.
 */
export default function NewAccountBanner({ pubkey }: NewAccountBannerProps) {
  const maturity = useAccountMaturity(pubkey);
  const [dismissed, setDismissed] = useState(() => {
    try { return localStorage.getItem(DISMISSED_KEY) === '1'; } catch { return false; }
  });
  const [expanded, setExpanded] = useState(false);

  // Don't show for established accounts, operators, or when loading
  if (!pubkey || maturity.loading || maturity.isEstablished || maturity.isOperator || dismissed) {
    return null;
  }

  const handleDismiss = () => {
    setDismissed(true);
    try { localStorage.setItem(DISMISSED_KEY, '1'); } catch {}
  };

  const daysRemaining = Math.max(0, ACCOUNT_AGE.restrictedDays - maturity.ageDays);

  return (
    <div className="mx-2 sm:mx-0 mb-3 rounded-xl border border-primary/20 bg-primary/5 overflow-hidden" style={{ contain: 'content' }}>
      <div className="flex items-start gap-3 px-4 py-3">
        <Shield className="w-5 h-5 text-primary shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-foreground">Welcome to BCH Nostr!</p>
          <p className="text-xs text-muted-foreground mt-0.5">
            To reduce spam, new accounts unlock additional features as they become established members of the community.
          </p>

          {expanded && (
            <div className="mt-3 space-y-2 text-xs">
              <p className="font-medium text-foreground">What you can do right now:</p>
              <ul className="space-y-1 text-muted-foreground">
                <li className="flex items-center gap-1.5"><Check className="w-3 h-3 text-emerald-500" /> Browse the platform</li>
                <li className="flex items-center gap-1.5"><Check className="w-3 h-3 text-emerald-500" /> Follow users</li>
                <li className="flex items-center gap-1.5"><Check className="w-3 h-3 text-emerald-500" /> Like posts</li>
                <li className="flex items-center gap-1.5"><Check className="w-3 h-3 text-emerald-500" /> Receive tips</li>
                <li className="flex items-center gap-1.5"><Check className="w-3 h-3 text-emerald-500" /> Edit your profile</li>
              </ul>

              <p className="font-medium text-foreground mt-2">Posting limits:</p>
              <ul className="space-y-1 text-muted-foreground">
                {maturity.isNewbie ? (
                  <li>• Max 1 post in your first 24 hours</li>
                ) : (
                  <li>• Max 3 posts per day during your first week</li>
                )}
                <li>• Normal posting after {daysRemaining} more day{daysRemaining === 1 ? '' : 's'}</li>
                <li>• Replies are always unlimited</li>
              </ul>

              <p className="font-medium text-foreground mt-2">Unlocks at {ACCOUNT_AGE.restrictedDays} days:</p>
              <ul className="space-y-1 text-muted-foreground">
                <li>• Unlimited posting</li>
                <li>• Create crowdfunding campaigns</li>
                <li>• List items on the marketplace</li>
              </ul>
            </div>
          )}

          <button
            onClick={() => setExpanded(!expanded)}
            className="flex items-center gap-1 text-xs text-primary hover:text-primary/80 mt-2 font-medium"
          >
            {expanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
            {expanded ? 'Show less' : 'Learn more'}
          </button>
        </div>

        <button
          onClick={handleDismiss}
          className="p-1 rounded-lg hover:bg-muted transition-colors shrink-0"
          title="Dismiss"
        >
          <X className="w-4 h-4 text-muted-foreground" />
        </button>
      </div>

      {/* Progress bar */}
      <div className="h-1 bg-muted">
        <div
          className="h-full bg-primary transition-all duration-500"
          style={{ width: `${Math.min(100, (maturity.ageDays / ACCOUNT_AGE.restrictedDays) * 100)}%` }}
        />
      </div>
    </div>
  );
}

